/**
 * useDemoSessionTracker hook
 * Tracks attempt counts and completed questions for free-tier users in a session.
 * Derives state from message history (survives page refresh).
 * Triggers auto-complete actions when demo limits are reached.
 *
 * US-005: Question and Attempt Limits with Auto-Complete
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createDecision } from '@/services/decisions';
import type { Message } from '@/types/session';

/**
 * Return type for useDemoSessionTracker hook
 */
export interface DemoSessionTrackerReturn {
  /** Number of answer submissions for the current question */
  currentQuestionAttempts: number;
  /** Number of questions completed in this session */
  questionsCompleted: number;
  /** Current question number (1-indexed, 0 if no questions started) */
  currentQuestionNumber: number;
  /** True if attempt limit was hit and auto-complete was triggered */
  isAttemptLimitReached: boolean;
  /** True if question limit was reached (session should complete) */
  isQuestionLimitReached: boolean;
  /** True while auto-complete API call is in progress */
  isAutoCompleting: boolean;
  /** Reason for showing demo popup, or null if no popup */
  demoPopupReason: 'attempt_limit' | 'question_limit' | null;
  /** Dismiss the demo limit popup */
  dismissDemoPopup: () => void;
}

/**
 * Hook to track demo session limits for free-tier users.
 * Derives attempt count and question count from message history for refresh survival.
 * Triggers auto-complete when the 5th attempt on a question is incorrect.
 *
 * @param sessionId - Current session ID
 * @param messages - Session messages from useSessionMessages
 * @param isFreeUser - True if user is on free tier
 * @param maxAttemptsPerQuestion - Max allowed attempts per question (null = unlimited)
 * @param maxQuestionsPerSession - Max allowed questions per session (null = unlimited)
 * @param userId - Current user's ID (needed for auto-complete API call)
 * @param mode - Session mode ('mcp' | 'serverless' | undefined)
 * @param serverlessQuestionJustCompleted - True when the serverless backend already completed
 *   the question (detected via pebblesAwarded in the TutorTurnResponse). When true in serverless
 *   mode, the auto-complete effect is suppressed to prevent a duplicate attempt_limit popup from
 *   firing alongside the CelebrationPopup. BUG-20260222-104604 - 2026-02-22
 */
export function useDemoSessionTracker(
  sessionId: string,
  messages: Message[] | undefined,
  isFreeUser: boolean,
  maxAttemptsPerQuestion: number | null,
  maxQuestionsPerSession: number | null,
  userId: string | undefined,
  mode?: 'mcp' | 'serverless',
  serverlessQuestionJustCompleted: boolean = false
): DemoSessionTrackerReturn {
  const [isAutoCompleting, setIsAutoCompleting] = useState(false);
  const [demoPopupReason, setDemoPopupReason] = useState<'attempt_limit' | 'question_limit' | null>(null);

  // Track which question numbers have already been auto-completed (prevent re-trigger on refresh)
  const autoCompletedQuestionsRef = useRef<Set<number>>(new Set());

  // Track whether the attempt limit auto-complete effect has fired for the current question
  // This prevents firing multiple times as messages array updates during polling
  const attemptAutoCompleteInProgressRef = useRef<boolean>(false);

  // Track whether the question limit has been detected (to show popup exactly once)
  const questionLimitDetectedRef = useRef<boolean>(false);

  // On mount, mark already-completed questions as processed so we don't re-trigger auto-complete
  // on page refresh when question_complete messages already exist
  const isInitializedRef = useRef(false);
  useEffect(() => {
    if (isInitializedRef.current) return;
    if (!messages) return;

    isInitializedRef.current = true;
    for (const msg of messages) {
      if (msg.decisionType === 'question_complete' && msg.questionNumber !== undefined) {
        autoCompletedQuestionsRef.current.add(msg.questionNumber);
      }
    }
  }, [messages]);

  // --- Derived state from message history (memoized) ---

  const currentQuestionNumber = useMemo(() => {
    if (!messages || messages.length === 0) return 0;
    const questionMessages = messages.filter((m) => m.decisionType === 'question');
    if (questionMessages.length === 0) return 0;
    // BUG-20260220-131006: When question messages exist but all have null/undefined
    // questionNumber (e.g. first question in MCP sessions before the create_decision.py
    // defaulting fix takes effect), treat them as questionNumber=1 rather than collapsing
    // to 0 via Math.max. This prevents the currentQuestionNumber===0 early-return guard
    // from silencing auto-complete for sessions that genuinely have questions - 2026-02-20
    const maxQNum = Math.max(...questionMessages.map((m) => m.questionNumber ?? 0));
    if (maxQNum <= 0) {
      // Question messages exist but none have a valid questionNumber — default to 1
      return 1;
    }
    return maxQNum;
  }, [messages]);

  const questionsCompleted = useMemo(() => {
    if (!messages) return 0;
    return messages.filter((m) => m.decisionType === 'question_complete').length;
  }, [messages]);

  const currentQuestionAttempts = useMemo(() => {
    if (!messages || currentQuestionNumber === 0) return 0;
    // BUG-20260220-131006: When currentQuestionNumber was derived from the null fallback
    // (i.e. it equals 1 because no question messages had a numeric questionNumber),
    // answer messages in the same session will also have null/undefined questionNumber.
    // Use a loose match: treat null/undefined questionNumber on an answer as matching
    // when currentQuestionNumber is 1 and the session appears to be in its first question.
    // This resolves the null !== 0 mismatch that caused attempt counting to always return 0
    // for MCP sessions where the first question has no questionNumber - 2026-02-20
    const questionsWithNumbers = messages.filter(
      (m) => m.decisionType === 'question' && m.questionNumber != null && m.questionNumber > 0
    );
    const sessionHasNumberedQuestions = questionsWithNumbers.length > 0;
    return messages.filter((m) => {
      if (m.role !== 'student' || m.decisionType !== 'answer') return false;
      // BUG-20260224-223842: Exclude optimistic temp-* messages from attempt count to prevent
      // auto-complete from firing during the pending mutation window - 2026-02-25
      // BUG-20260616-221535: Optional-chain guard mirrors the primary fix in useSubmitAnswer.ts;
      // a null/undefined-id answer is treated as non-temp and counted as a real attempt. - 2026-06-17
      if (m.id?.startsWith('temp-')) return false;
      if (m.questionNumber === currentQuestionNumber) return true;
      // Loose match: if the session has no numbered questions and currentQuestionNumber
      // is 1 (the null-derived fallback), count answers with null/undefined questionNumber
      if (!sessionHasNumberedQuestions && currentQuestionNumber === 1 && m.questionNumber == null) {
        return true;
      }
      return false;
    }).length;
  }, [messages, currentQuestionNumber]);

  // Whether the current question already has a question_complete message
  const hasQuestionCompleteForCurrentQ = useMemo(() => {
    if (!messages || currentQuestionNumber === 0) return false;
    return messages.some(
      (m) => m.decisionType === 'question_complete' && m.questionNumber === currentQuestionNumber
    );
  }, [messages, currentQuestionNumber]);

  // The last tutor message in the session
  const lastTutorMessage = useMemo(() => {
    if (!messages) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'tutor') return messages[i];
    }
    return null;
  }, [messages]);

  // Derived limit flags
  const isAttemptLimitReached =
    isFreeUser &&
    maxAttemptsPerQuestion !== null &&
    currentQuestionAttempts >= maxAttemptsPerQuestion &&
    hasQuestionCompleteForCurrentQ;

  const isQuestionLimitReached =
    isFreeUser &&
    maxQuestionsPerSession !== null &&
    questionsCompleted >= maxQuestionsPerSession;

  // Auto-complete question when attempt limit is reached and tutor responded with feedback
  useEffect(() => {
    if (!isFreeUser) return;
    if (!maxAttemptsPerQuestion) return;
    if (!userId) return;
    if (!messages || messages.length === 0) return;
    if (currentQuestionNumber === 0) return;

    // Check: attempt limit reached for current question
    if (currentQuestionAttempts < maxAttemptsPerQuestion) return;

    // BUG-20260226-163519: Skip createDecision auto-complete for serverless sessions.
    // Serverless sessions rely on backend force-complete in tutor_turn.py which
    // correctly advances currentQuestionIndex and injects Q2 text. The createDecision
    // call below only writes to the Decisions table and cannot advance serverless
    // session state (currentQuestionIndex lives in the Sessions table and is updated
    // by the tutor_turn handler, not by createDecision). This replaces the earlier
    // BUG-20260222-104604 serverlessQuestionJustCompleted guard which only suppressed
    // auto-complete when the backend had already signalled completion — the new guard
    // is simpler and covers all serverless cases. - 2026-02-26
    if (mode === 'serverless') return;

    // Check: question_complete doesn't already exist for this question
    if (hasQuestionCompleteForCurrentQ) return;

    // Check: this question hasn't already been auto-completed by us
    if (autoCompletedQuestionsRef.current.has(currentQuestionNumber)) return;

    // Check: we're not already running an auto-complete
    if (attemptAutoCompleteInProgressRef.current) return;

    // Check: last tutor message is feedback (not question_complete) for current question
    // This means the 5th answer was incorrect — auto-complete is needed
    if (!lastTutorMessage) return;
    if (lastTutorMessage.decisionType !== 'feedback') return;
    // Ensure the feedback is for the current question
    if (
      lastTutorMessage.questionNumber !== undefined &&
      lastTutorMessage.questionNumber !== currentQuestionNumber
    ) return;

    // Mark as in-progress to prevent concurrent triggers
    attemptAutoCompleteInProgressRef.current = true;
    autoCompletedQuestionsRef.current.add(currentQuestionNumber);

    const questionNumToComplete = currentQuestionNumber;
    const attemptCountToReport = maxAttemptsPerQuestion;

    const runAutoComplete = async () => {
      setIsAutoCompleting(true);
      try {
        await createDecision({
          userId,
          sessionId,
          role: 'tutor',
          decisionType: 'question_complete',
          content: `You've used all ${maxAttemptsPerQuestion ?? 5} attempts for this question. Let's move on!`,
          attemptCount: attemptCountToReport,
          questionNumber: questionNumToComplete,
        });
        // Show attempt limit popup after successful auto-complete
        setDemoPopupReason('attempt_limit');
      } catch (err) {
        console.error('[useDemoSessionTracker] Auto-complete question failed:', err);
        // Remove from completed set so it can retry on next message update
        autoCompletedQuestionsRef.current.delete(questionNumToComplete);
        attemptAutoCompleteInProgressRef.current = false;
      } finally {
        setIsAutoCompleting(false);
        attemptAutoCompleteInProgressRef.current = false;
      }
    };

    runAutoComplete();
  }, [
    isFreeUser,
    maxAttemptsPerQuestion,
    userId,
    sessionId,
    currentQuestionNumber,
    currentQuestionAttempts,
    hasQuestionCompleteForCurrentQ,
    lastTutorMessage,
    messages,
    mode,
    serverlessQuestionJustCompleted,
  ]);

  // Show question limit popup when question limit is reached (once)
  useEffect(() => {
    if (!isFreeUser) return;
    if (!maxQuestionsPerSession) return;
    if (questionsCompleted < maxQuestionsPerSession) return;
    if (questionLimitDetectedRef.current) return;

    questionLimitDetectedRef.current = true;
    // Only show question_limit popup if we're not already showing attempt_limit
    // (for AC-005.5 — both limits cascade: attempt_limit popup shows first, then
    // session auto-completes and question_limit is handled by session completion)
    // We set question_limit here regardless — SessionChatPage decides which to prioritize
    setDemoPopupReason('question_limit');
  }, [isFreeUser, maxQuestionsPerSession, questionsCompleted]);

  const dismissDemoPopup = useCallback(() => {
    setDemoPopupReason(null);
  }, []);

  return {
    currentQuestionAttempts,
    questionsCompleted,
    currentQuestionNumber,
    isAttemptLimitReached,
    isQuestionLimitReached,
    isAutoCompleting,
    demoPopupReason,
    dismissDemoPopup,
  };
}
